/**
 * Helpers to derive note/attachment for the transaction API from custom fields,
 * and to merge transaction.note/attachment into customFieldValues when loading for edit.
 */
import type {
  TransactionCustomField,
  TransactionCustomFieldValues,
  TransactionCustomFieldScopeType,
  CustomFieldAttachmentValue,
} from "../types";

/** True if this field applies to the given transaction type (supports transactionType and legacy transactionTypes). */
export function fieldAppliesToScope(
  field: TransactionCustomField,
  scope: TransactionCustomFieldScopeType
): boolean {
  if (field.transactionType != null) return field.transactionType === scope;
  if (field.transactionTypes && field.transactionTypes.length > 0) return field.transactionTypes.includes(scope);
  return false;
}

export function deriveNoteAndAttachmentFromCustomFields(
  transactionCustomFields: TransactionCustomField[],
  scope: TransactionCustomFieldScopeType,
  customFieldValues: TransactionCustomFieldValues | undefined
): { note: string; attachmentUrl: string | null; attachmentName: string | null; attachmentType: string | null } {
  const ordered = [...(transactionCustomFields || [])]
    .filter((f) => fieldAppliesToScope(f, scope))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const firstTextId = ordered.find((f) => f.type === "text")?.id;
  const firstAttachmentId = ordered.find((f) => f.type === "attachment")?.id;
  const note =
    firstTextId && customFieldValues?.[firstTextId] != null && typeof customFieldValues[firstTextId] === "string"
      ? (customFieldValues[firstTextId] as string)
      : "";
  let attachmentUrl: string | null = null;
  let attachmentName: string | null = null;
  let attachmentType: string | null = null;
  if (
    firstAttachmentId &&
    customFieldValues?.[firstAttachmentId] != null &&
    typeof customFieldValues[firstAttachmentId] === "object" &&
    "url" in (customFieldValues[firstAttachmentId] as object)
  ) {
    const att = customFieldValues[firstAttachmentId] as CustomFieldAttachmentValue;
    attachmentUrl = att.url ?? null;
    attachmentName = att.name ?? null;
    attachmentType = att.type ?? null;
  }
  return { note, attachmentUrl, attachmentName, attachmentType };
}

export function mergeNoteAndAttachmentIntoCustomFieldValues(
  transactionCustomFields: TransactionCustomField[],
  scope: TransactionCustomFieldScopeType,
  existing: TransactionCustomFieldValues | undefined,
  note: string | undefined,
  attachmentUrl: string | null | undefined,
  attachmentName: string | null | undefined,
  attachmentType: string | null | undefined
): TransactionCustomFieldValues {
  const ordered = [...(transactionCustomFields || [])]
    .filter((f) => fieldAppliesToScope(f, scope))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const firstTextId = ordered.find((f) => f.type === "text")?.id;
  const firstAttachmentId = ordered.find((f) => f.type === "attachment")?.id;
  const out: TransactionCustomFieldValues = { ...(existing || {}) };
  if (firstTextId && note != null && note !== "") {
    out[firstTextId] = note;
  }
  if (firstAttachmentId && attachmentUrl) {
    out[firstAttachmentId] = {
      url: attachmentUrl,
      name: attachmentName || "",
      type: attachmentType || "",
    } as CustomFieldAttachmentValue;
  }
  return out;
}
